/**
 * CRM Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as crmService from '../api/services/crm.service';
import type {
  CrmLeadCreateRequest,
  CrmLeadUpdateRequest,
} from '../types';
import { toast } from '../stores/toast.store';

// Query keys
const KEYS = {
  leads: ['crm', 'leads'] as const,
  lead: (id: number) => ['crm', 'leads', id] as const,
  stages: ['crm', 'stages'] as const,
};

interface UseLeadsParams {
  offset?: number;
  limit?: number;
  state?: string;
  userId?: number;
  search?: string;
  myLeads?: boolean;
}

/**
 * Hook for fetching CRM leads list
 */
export function useLeads(params: UseLeadsParams = {}) {
  return useQuery({
    queryKey: [...KEYS.leads, params],
    queryFn: async () => {
      const result = await crmService.getLeads({
        offset: params.offset,
        limit: params.limit,
        state: params.state,
        user_id: params.userId,
        search: params.search,
        my_leads: params.myLeads,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch leads');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for fetching single CRM lead
 */
export function useLead(leadId: number | undefined) {
  return useQuery({
    queryKey: KEYS.lead(leadId!),
    queryFn: async () => {
      const result = await crmService.getLead(leadId!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch lead');
      }
      return result.data!;
    },
    enabled: !!leadId,
  });
}

/**
 * Hook for fetching CRM stages
 */
export function useStages() {
  return useQuery({
    queryKey: KEYS.stages,
    queryFn: async () => {
      const result = await crmService.getStages();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch stages');
      }
      return result.data!;
    },
  });
}

/**
 * Hook for creating CRM lead
 */
export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CrmLeadCreateRequest) => {
      const result = await crmService.createLead(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create lead');
      }
      return result.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.leads });
      toast.success('商機建立成功');
    },
    onError: (error: Error) => {
      toast.error('建立失敗', error.message);
    },
  });
}

/**
 * Hook for updating CRM lead
 */
export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, data }: { leadId: number; data: CrmLeadUpdateRequest }) => {
      const result = await crmService.updateLead(leadId, data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update lead');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.leads });
      queryClient.setQueryData(KEYS.lead(data.id), data);
      toast.success('商機更新成功');
    },
    onError: (error: Error) => {
      toast.error('更新失敗', error.message);
    },
  });
}

/**
 * Hook for uploading attachment
 */
export function useUploadLeadAttachment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      file,
      attachmentType,
    }: {
      leadId: number;
      file: File;
      attachmentType?: string;
    }) => {
      const result = await crmService.uploadLeadAttachment(leadId, file, attachmentType);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to upload');
      }
      return result.data!;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: KEYS.lead(variables.leadId) });
      toast.success('上傳成功');
    },
    onError: (error: Error) => {
      toast.error('上傳失敗', error.message);
    },
  });
}
